import {
  Delete20Regular,
  Edit20Regular,
  Eye20Regular,
} from "@fluentui/react-icons";
import { mergeClasses } from "@fluentui/react-components";
import { deleteUseCase } from "api";
import { ConstantValues } from "common/constants";
import { ActionDialog } from "components/actionDialog/ActionDialog";
import { AdminListPage } from "components/adminList/AdminListPage";
import { ExportIcon } from "icons/ExportIcon";
import { PlusIcon } from "icons/PlusIcon";
import { SearchIcon } from "icons/SearchIcon";
import {
  useSharedAdminContentHeaderStyles,
  useSharedButtonStyles,
  useSharedAdminCellStyles,
} from "theme/sharedStyles";
import { AdminRowAction } from "components/adminList/AdminListPage.types";
import { useAdminColumnStyles } from "components/adminList/AdminTable.styles";
import { findSidebarItem } from "components/adminSidebar/adminSidebarConfig";
import { useAdminListPagination } from "hooks/useAdminListPagination";
import { useAdminListSearch } from "hooks/useAdminListSearch";
import { useAdminListSort } from "hooks/useAdminListSort";
import { ITableColumn } from "models/ITableColumn";
import { IUseCase } from "models/IUseCase";
import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store";
import { formatDate, generateCSV, downloadCSV } from "utils/generalUtils";
import { removeUseCase } from "../../redux/useCasesSlice";
import { EditUseCase } from "./editDialog/EditUseCase";
import { ViewUseCase } from "./viewDialog/ViewUseCase";

export const ManageUseCases = (): JSX.Element => {
  const adminCellStyles = useSharedAdminCellStyles();
  const colStyles = useAdminColumnStyles();
  const headerClasses = useSharedAdminContentHeaderStyles();
  const buttonStyles = useSharedButtonStyles();
  const dispatch = useDispatch();
  const useCases = useSelector(
    (state: RootState) => state.useCasesSlice.value,
  ).filter((uc) => uc.title !== ConstantValues.OTHER_USECASE_NAME);

  const [viewingUseCase, setViewingUseCase] = useState<IUseCase | null>(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState<boolean>(false);
  const [isEditPopupOpen, setIsEditPopupOpen] = useState<boolean>(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
  const [selectedUseCase, setSelectedUseCase] = useState<IUseCase | null>(null);
  const columns: ITableColumn<IUseCase>[] = useMemo(
    () => [
      {
        columnId: "title",
        displayValue: "Title",
        className: colStyles.normal,
        renderHeaderCell: () => "Title",
        compare: (a: IUseCase, b: IUseCase) => a.title.localeCompare(b.title),
        renderCell: (useCase: IUseCase): JSX.Element => (
          <span className={adminCellStyles.nameText} title={useCase.title}>
            {useCase.title}
          </span>
        ),
      },
      {
        columnId: "displayName",
        displayValue: "Author",
        className: colStyles.normal,
        renderHeaderCell: () => "Author",
        compare: (a: IUseCase, b: IUseCase) =>
          a.user.displayName.localeCompare(b.user.displayName),
        renderCell: (useCase: IUseCase): JSX.Element => (
          <span title={useCase.user?.displayName}>
            {useCase.user?.displayName || "-"}
          </span>
        ),
      },
      {
        columnId: "updatedAt",
        displayValue: "Last Updated",
        className: colStyles.normal,
        renderHeaderCell: () => "Last Updated",
        compare: (a: IUseCase, b: IUseCase) => a.updatedAt - b.updatedAt,
        renderCell: (useCase: IUseCase): JSX.Element => (
          <span>{formatDate(useCase.updatedAt)}</span>
        ),
      },
    ],
    [adminCellStyles, colStyles],
  );

  const {
    sortColumn,
    isSortDescending,
    sortItems,
    onSortColumnChanged,
    onClearColumnChanged,
  } = useAdminListSort<IUseCase>({
    cacheKey: "UseCases",
    defaultSortColumn: "updatedAt",
    defaultDescending: true,
  });

  const searchFields = useMemo(() => ["title"] as (keyof IUseCase)[], []);
  const {
    searchQuery: internalSearchQuery,
    handleSearchChange,
    handleSearchValueChange,
    filteredItems,
  } = useAdminListSearch<IUseCase>({
    items: useCases,
    searchFields,
  });

  const sortedItems = useMemo(
    () => sortItems(filteredItems, columns),
    [filteredItems, sortItems, columns],
  );

  const emptyStateMessage = useMemo(() => {
    return internalSearchQuery
      ? "No use cases match your search."
      : "No use cases found";
  }, [internalSearchQuery]);

  const {
    paginatedItems,
    selectedPageIndex,
    pageCount,
    handlePageChange,
    totalItemsCount,
    itemsPerPage,
  } = useAdminListPagination<IUseCase>({
    items: sortedItems,
    itemsPerPage: 10,
  });

  const handleViewClick = (useCase: IUseCase): void => {
    setViewingUseCase(useCase);
    setIsViewDialogOpen(true);
  };

  const handleEditClick = (useCase: IUseCase): void => {
    setSelectedUseCase(useCase);
    setIsEditPopupOpen(true);
  };

  const handleDeleteClick = (useCase: IUseCase): void => {
    setSelectedUseCase(useCase);
    setShowDeleteDialog(true);
  };

  const handleCreateClick = (): void => {
    setSelectedUseCase(null);
    setIsEditPopupOpen(true);
  };

  const handleExport = useCallback((): void => {
    const csv = generateCSV(sortedItems, [
      { header: "Title", accessor: (uc) => uc.title },
      { header: "Author", accessor: (uc) => uc.user?.displayName || "" },
      { header: "Last Updated", accessor: (uc) => formatDate(uc.updatedAt) },
    ]);
    downloadCSV(csv, "use-cases.csv");
  }, [sortedItems]);

  const handleEditDismiss = (isSaved: boolean): void => {
    setIsEditPopupOpen(false);
    if (isSaved) {
      setSelectedUseCase(null);
    }
  };

  const handleDeleteDismiss = (): void => {
    setShowDeleteDialog(false);
  };

  const handleDeleteConfirmed = async (): Promise<void> => {
    if (!selectedUseCase?.id) return;

    try {
      await deleteUseCase({ useCaseId: selectedUseCase.id });
      dispatch(removeUseCase(selectedUseCase.id));
      setSelectedUseCase(null);
    } catch (error) {
      console.error("Error deleting useCase:", error);
    } finally {
      setShowDeleteDialog(false);
    }
  };

  const rowActions: AdminRowAction<IUseCase>[] = useMemo(
    () => [
      { icon: <Eye20Regular />, tooltip: "View", onClick: handleViewClick },
      { icon: <Edit20Regular />, tooltip: "Edit", onClick: handleEditClick },
      {
        icon: <Delete20Regular />,
        tooltip: "Delete",
        onClick: handleDeleteClick,
      },
    ],
    [],
  );

  const pageTitle = findSidebarItem("useCases")?.label ?? "Use Cases";

  return (
    <>
      <div className={headerClasses.root}>
        <div
          className={mergeClasses(
            headerClasses.titleRow,
            headerClasses.titleRowWithFilters,
          )}
        >
          <h1 className={headerClasses.title}>{pageTitle}</h1>
          <div className={headerClasses.actions}>
            <button
              className={mergeClasses(
                headerClasses.actionButton,
                buttonStyles.secondaryButton,
              )}
              onClick={handleExport}
              type="button"
            >
              <ExportIcon /> Export
            </button>
            <button
              className={mergeClasses(
                headerClasses.actionButton,
                buttonStyles.primaryButton,
              )}
              onClick={handleCreateClick}
              type="button"
            >
              <PlusIcon /> Add New
            </button>
          </div>
        </div>
        <div className={headerClasses.filterRow}>
          <div className={headerClasses.searchInputWrapper}>
            <SearchIcon className={headerClasses.searchIcon} />
            <input
              type="text"
              className={headerClasses.searchInput}
              placeholder="Search title..."
              value={internalSearchQuery}
              onChange={(e) => handleSearchValueChange(e.target.value)}
              aria-label="Search title..."
            />
          </div>
        </div>
      </div>

      <AdminListPage<IUseCase>
        title={pageTitle}
        items={useCases}
        columns={columns}
        isLoading={false}
        headerActions={[]}
        searchPlaceholder="Search title..."
        searchQuery={internalSearchQuery}
        onSearchChange={handleSearchChange}
        hideHeader={true}
        rowActions={rowActions}
        getRowId={(useCase) => useCase.id}
        sortColumn={sortColumn}
        isSortDescending={isSortDescending}
        onSortChange={onSortColumnChanged}
        onSortClear={onClearColumnChanged}
        paginatedItems={paginatedItems}
        selectedPageIndex={selectedPageIndex}
        pageCount={pageCount}
        totalItemsCount={totalItemsCount}
        itemsPerPage={itemsPerPage}
        onPageChange={handlePageChange}
        emptyStateMessage={emptyStateMessage}
      />

      {isViewDialogOpen && viewingUseCase && (
        <ViewUseCase
          isOpen={isViewDialogOpen}
          onDismiss={() => {
            setIsViewDialogOpen(false);
            setViewingUseCase(null);
          }}
          useCase={viewingUseCase}
        />
      )}

      {isEditPopupOpen && (
        <EditUseCase
          isOpen={isEditPopupOpen}
          onDismiss={handleEditDismiss}
          useCase={selectedUseCase}
        />
      )}

      <ActionDialog
        isVisible={showDeleteDialog}
        title="Delete Use Case"
        message="Are you sure you want to delete this Use Case?"
        confirmationMessage="Delete"
        dismissMessage="Cancel"
        onConfirmation={handleDeleteConfirmed}
        onDismiss={handleDeleteDismiss}
      />
    </>
  );
};
